"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function YourSunlitSanctuary() {
  const {
    ref: textRef,
    isVisible: textVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="1055" id="your-sunlit-sanctuary" className="relative bg-background py-24 md:py-44 overflow-hidden">
      <div className="mx-auto max-w-[1320px] px-5 md:px-10 lg:px-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {}
          <div ref={textRef} className="lg:col-span-5 flex flex-col">
            {}
            <div className={`flex items-center gap-2 transition-all duration-700 ease-[cubic-bezier(.7,0,.2,1)] ${textVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            transitionDelay: "0ms"
          }}>
              <span className="font-mono text-xs uppercase text-muted-foreground" style={{
              letterSpacing: "0.18em"
            }}>
                № 01 — Pristine Living
              </span>
              <span className="inline-block w-2 h-2 rounded-lg bg-[var(--chart-1)]" aria-hidden="true" />
            </div>

            {}
            <h1 className={`mt-8 font-default font-semibold text-foreground transition-all duration-700 ease-[cubic-bezier(.7,0,.2,1)] ${textVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            fontSize: "clamp(3rem, 7.4vw, 6.75rem)",
            lineHeight: 1.02,
            letterSpacing: "-0.025em",
            transitionDelay: "80ms"
          }}>
              Expertly cleaned,{" "}
              <span className="relative inline-block italic font-serif">
                <span className="relative z-10">restored</span>
                <span aria-hidden="true" className="absolute left-0 bottom-1 h-[2px] bg-[var(--chart-1)] origin-left" style={{
                width: textVisible ? "100%" : "0%",
                transition: "width 480ms cubic-bezier(.7,0,.2,1)",
                transitionDelay: "600ms"
              }} />
              </span>{" "}
              to perfection.
            </h1>

            {}
            <div className={`mt-8 transition-all duration-700 ease-[cubic-bezier(.7,0,.2,1)] ${textVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            transitionDelay: "160ms"
          }} aria-hidden="true">
              <svg width="64" height="22" viewBox="0 0 64 22" fill="none" className="text-muted-foreground/40">
                <path d="M0 4 Q 16 0, 32 4 T 64 4" stroke="currentColor" strokeWidth="1" fill="none" />
                <path d="M0 11 Q 16 7, 32 11 T 64 11" stroke="currentColor" strokeWidth="1" fill="none" />
                <path d="M0 18 Q 16 14, 32 18 T 64 18" stroke="currentColor" strokeWidth="1" fill="none" />
              </svg>
            </div>

            {}
            <p className={`mt-8 font-default text-foreground/85 max-w-[42ch] transition-all duration-700 ease-[cubic-bezier(.7,0,.2,1)] ${textVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            fontSize: "clamp(1.05rem, 1.25vw, 1.2rem)",
            lineHeight: 1.62,
            transitionDelay: "240ms"
          }}>
              At Sparkle & Shine Maids, we transform your home into a breathable sanctuary. From deep cleaning to recurring maintenance, our professional team ensures every corner reflects pristine quality.
            </p>

            {}
            <div className={`mt-10 flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-8 transition-all duration-700 ease-[cubic-bezier(.7,0,.2,1)] ${textVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            transitionDelay: "320ms"
          }}>
              <Button asChild size="lg" className="group relative overflow-hidden rounded-lg bg-foreground text-background px-7 py-3.5 h-auto font-default text-sm font-medium hover:bg-foreground">
                <Link to="/GetInTouchPage">
                  <span aria-hidden="true" className="absolute inset-0 bg-[var(--chart-1)] -translate-x-full group-hover:translate-x-0 transition-transform duration-[320ms] ease-[cubic-bezier(.7,0,.2,1)]" />
                  <span className="relative z-10 flex items-center gap-2">
                    Get an Instant Quote
                    <ArrowRight className="size-4 transition-transform duration-[220ms] ease-[cubic-bezier(.7,0,.2,1)] group-hover:translate-x-1" />
                  </span>
                </Link>
              </Button>

              <Link to="/OurCleaningMenuPage" className="group relative inline-flex items-center font-default text-sm font-medium text-foreground self-start sm:self-auto">
                <span className="relative pb-1">
                  View our services
                  <span aria-hidden="true" className="absolute left-0 bottom-0 h-[1px] w-0 bg-[var(--chart-1)] group-hover:w-full transition-[width] duration-[240ms] ease-[cubic-bezier(.7,0,.2,1)]" />
                </span>
              </Link>
            </div>
          </div>

          {}
          <div ref={imageRef} className={`lg:col-span-7 relative lg:-mr-12 transition-opacity duration-700 ease-[cubic-bezier(.7,0,.2,1)] ${imageVisible ? "opacity-100" : "opacity-0"}`}>
            <div className="relative">
              {}
              <div className="relative aspect-[4/5] w-full overflow-hidden bg-card">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700217/img/sunlit_spotless_home.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700217/img/sunlit_spotless_home.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700217/img/sunlit_spotless_home.webp-wvc-sizes" alt="A bright, sunlit living room with spotless surfaces" loading="lazy" decoding="async" className="h-full w-full object-cover" />
              </div>

              {}
              <div className="hidden md:block absolute top-4 right-4 max-w-[180px] text-right" aria-hidden="true">
                <p className="font-serif italic font-light text-foreground/80 text-[13px] leading-snug">
                  <span className="mr-1">↳</span>
                  pristine standards — verified quality
                </p>
              </div>

              {}
              <div className="absolute bottom-6 -left-2 md:-left-8 flex items-center gap-2">
                <span className="font-mono text-[11px] uppercase text-foreground/70" style={{
                letterSpacing: "0.14em"
              }}>
                  EST. 2024 / TRUSTED SERVICE
                </span>
                <span className="inline-block w-2 h-2 rounded-lg bg-[var(--chart-1)]" aria-hidden="true" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {}
      <div aria-hidden="true" className="hidden xl:flex absolute right-6 top-1/2 -translate-y-1/2 items-center gap-2 pointer-events-none" style={{
      transform: "translateY(-50%) rotate(-90deg)",
      transformOrigin: "center"
    }}>
        <span className="font-mono text-[11px] uppercase text-muted-foreground whitespace-nowrap" style={{
        letterSpacing: "0.18em"
      }}>
          SCROLL
        </span>
        <span className="inline-block w-1.5 h-1.5 rounded-lg bg-[var(--chart-1)]" />
      </div>
    </section>;
}